/**
 * PostHog storefront funnel events — issue #67 (per-PDP x source conversion funnel).
 *
 * Pairs with the base install in snippets/posthog.liquid (issue #62), which loads
 * PostHog in <head> and registers traffic source (utm_* / fbclid / gclid, first-touch)
 * and landing context (shopify_template / page_type / product_handle / product_id)
 * as SUPER PROPERTIES. Those ride along on every capture() below automatically, so
 * we deliberately do NOT re-tag source/PDP here — that is exactly what lets the
 * funnel slice "Meta ads -> PDP X -> add_to_cart" and flag a broken-PDP regression.
 *
 * Funnel step 1 (product viewed) is the base snippet's $pageview where
 * shopify_page_type == 'product' — no separate event needed.
 *
 * Captured here (storefront only; the hosted Shopify checkout is a separate Web
 * Pixel, issue #64 — Shopify Advanced can't run theme JS on checkout):
 *   - variant_selected    PDP variant picker change         (theme pub/sub: variantChange)
 *   - add_to_cart         standard <product-form> success  (theme pub/sub: cartUpdate)
 *   - add_to_cart_failed  standard <product-form> failure  (theme pub/sub: cartError)
 *   - initiate_checkout   cart drawer / cart page / notification checkout button
 *   - initiate_checkout   seafood-plan single-flow custom checkout (direct redirect)
 *
 * DECISION (2026-06-24): no checkout-stage Web Pixel (issue #64) — deliberately out
 * of scope. This funnel intentionally ENDS at initiate_checkout (the click to
 * /checkout). Rationale:
 *   1. Not needed for the goal. The reason this initiative exists is to catch a
 *      broken-PDP-on-paid-traffic regression (a Meta-ads PDP where variant-select /
 *      add-to-cart silently die). That failure surfaces BEFORE checkout — as PDP
 *      pageviews with add_to_cart / initiate_checkout dropping to ~zero — which the
 *      storefront funnel above already detects. No checkout data required.
 *   2. Additive, not load-bearing. Triple Whale is the authoritative purchase /
 *      revenue / order-attribution source. A PostHog checkout pixel would only make
 *      PostHog's OWN funnel self-contained (initiate_checkout -> purchase); it would
 *      not be a source of truth for revenue.
 *   3. It's real, separate work. Shopify Advanced can't run theme JS on the hosted
 *      checkout, so #64 means a sandboxed Shopify Web Pixel (Admin -> Customer
 *      events) subscribing to checkout_started / payment_info_submitted /
 *      checkout_completed. The hard part is IDENTITY STITCHING: the pixel sandbox
 *      can't read this storefront's ph_* cookie, so linking checkout events to the
 *      same PostHog person/session is non-trivial. If #64 is ever revisited, design
 *      that identity bridge FIRST — otherwise checkout events arrive orphaned.
 *
 * Posture: NEVER THROW. Analytics must never break the storefront. Every handler is
 * wrapped; a missing PostHog or missing theme global degrades to a silent no-op.
 *
 * TODO (fast-follow, issue #67): RecipeKit quick-add (/cart/add.js fetch path) and
 * an explicit seafood-plan add_to_cart step are not yet captured.
 */
(function () {
  'use strict';

  function ph() {
    return (typeof window !== 'undefined' && window.posthog) || null;
  }

  // Safe capture: PostHog's stub queues calls until the async lib loads, so this is
  // safe to call early. Wrapped so a capture error can never bubble into the store.
  function capture(event, props) {
    try {
      var p = ph();
      if (p && typeof p.capture === 'function') p.capture(event, props || {});
    } catch (e) {
      if (window.console && console.debug) console.debug('posthog capture skipped:', event, e);
    }
  }

  // Shopify line-item prices are integer cents.
  function toDollars(cents) {
    var n = Number(cents);
    return isFinite(n) ? n / 100 : undefined;
  }

  function variantIdString(id) {
    return id != null ? String(id) : undefined;
  }

  // --- add_to_cart / add_to_cart_failed via the theme's native pub/sub bus --------
  // pubsub.js declares `subscribe`/`publish` (function decls -> on window). constants.js
  // declares `PUB_SUB_EVENTS` as a top-level `const` -> a bareword global, NOT a window
  // property. typeof on a bareword is ReferenceError-safe in strict mode, so guard both.
  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        try {
          // Only PDP <product-form> adds; cart-drawer qty edits also publish cartUpdate
          // with a different source and must not count as add_to_cart.
          if (!event || event.source !== 'product-form') return;

          // cartData is the /cart/add.js response. Its shape varies across Shopify
          // versions ({ items: [...] } vs a bare line-item object), so read defensively.
          var data = event.cartData || {};
          var item;
          if (Array.isArray(data.items)) {
            item =
              data.items.filter(function (i) {
                return i && String(i.variant_id) === String(event.productVariantId);
              })[0] || data.items[data.items.length - 1];
          } else {
            item = data;
          }
          item = item || {};

          capture('add_to_cart', {
            variant_id: variantIdString(event.productVariantId),
            product_id: item.product_id,
            product_title: item.product_title || item.title,
            price: toDollars(item.price),
            quantity: item.quantity,
            currency: 'USD',
            source: 'product_form',
          });
        } catch (e) {
          if (window.console && console.debug) console.debug('posthog add_to_cart skipped:', e);
        }
      });

      subscribe(PUB_SUB_EVENTS.cartError, function (event) {
        try {
          if (!event || event.source !== 'product-form') return;
          capture('add_to_cart_failed', {
            variant_id: variantIdString(event.productVariantId),
            message: event.message,
            source: 'product_form',
          });
        } catch (e) {
          if (window.console && console.debug) console.debug('posthog add_to_cart_failed skipped:', e);
        }
      });

      // --- variant_selected via the theme's variantChange pub/sub --------------------
      // global.js publishes variantChange ONLY on a real user option change (it is bound
      // to the picker's `change` event, not initial render), so this is already debounced
      // to one event per selection — no extra throttling needed. A picker that silently
      // breaks stops publishing, so variant_selected drops to ~0 for that PDP: this is the
      // funnel step (PDP view -> variant_selected -> add_to_cart) that separates a broken
      // variant picker from a broken add-to-cart button. Payload shape (global.js) is
      // { data: { sectionId, html, variant } } where `variant` is the Shopify variant JSON
      // (price in integer cents, like add_to_cart above) or null for an unavailable combo.
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        try {
          var variant = (event && event.data && event.data.variant) || null;
          capture('variant_selected', {
            variant_id: variant ? variantIdString(variant.id) : undefined,
            variant_title: variant ? variant.title : undefined,
            price: variant ? toDollars(variant.price) : undefined,
            available: variant ? variant.available : undefined,
            source: 'variant_selects',
          });
        } catch (e) {
          if (window.console && console.debug) console.debug('posthog variant_selected skipped:', e);
        }
      });
    }
  } catch (e) {
    if (window.console && console.debug) console.debug('posthog pub/sub wiring skipped:', e);
  }

  // --- initiate_checkout via delegated click (capture phase, before navigation) ----
  // Standard checkout buttons across cart drawer (cart-drawer.liquid), cart page
  // (main-cart-footer.liquid) and cart notification (cart-notification.liquid) all use
  // name="checkout". The seafood-plan single-flow uses a custom .checkout-button that
  // clears the cart, adds items, and redirects straight to /checkout.
  try {
    document.addEventListener(
      'click',
      function (evt) {
        try {
          var target = evt.target;
          if (!target || typeof target.closest !== 'function') return;

          if (target.closest('button[name="checkout"]')) {
            capture('initiate_checkout', { checkout_method: 'standard_cart' });
            return;
          }
          if (target.closest('.mainButtonArea .checkout-button')) {
            capture('initiate_checkout', {
              checkout_method: 'seafood_plan',
              is_subscription: true,
            });
          }
        } catch (e) {
          if (window.console && console.debug) console.debug('posthog initiate_checkout skipped:', e);
        }
      },
      true
    );
  } catch (e) {
    if (window.console && console.debug) console.debug('posthog checkout listener skipped:', e);
  }
})();
